// Thin wrapper around the browser Web Speech API (STT + TTS).
// Nothing is uploaded — recognition runs locally in the browser.

const SR = typeof window !== "undefined"
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : null;

export const isSTTSupported = () => !!SR;
export const isTTSSupported = () =>
  typeof window !== "undefined" && "speechSynthesis" in window;

/* -------------------- Voice selection -------------------- */
//
// Priority order (per Fase 6 brief):
//   1. es-MX
//   2. es-ES
//   3. es-US
//   4. any es-* (Latin fallbacks like es-419, es-CO, etc.)
//   5. default (browser picks)
//
// Voices in Chrome/Edge/iOS Safari load asynchronously — we cache the pick and
// invalidate the cache on `onvoiceschanged`.

let cachedVoice = null;
let voicesReady = false;

function _rescan() {
  if (!isTTSSupported()) return null;
  const voices = window.speechSynthesis.getVoices() || [];
  if (voices.length === 0) {
    cachedVoice = null;
    return null;
  }
  voicesReady = true;
  cachedVoice =
    voices.find((v) => /^es-MX/i.test(v.lang)) ||
    voices.find((v) => /^es-ES/i.test(v.lang)) ||
    voices.find((v) => /^es-US/i.test(v.lang)) ||
    voices.find((v) => /^es(-|_|$)/i.test(v.lang)) ||
    null;
  return cachedVoice;
}

/** Returns the best available Spanish voice, or null if none. */
export function pickSpanishVoice() {
  if (cachedVoice) return cachedVoice;
  return _rescan();
}

// Warm up voices — some browsers load them async, some load them lazily on
// first `getVoices()`. We hook `onvoiceschanged` if available.
if (isTTSSupported()) {
  try {
    _rescan();
    if (typeof window.speechSynthesis.addEventListener === "function") {
      window.speechSynthesis.addEventListener("voiceschanged", () => {
        cachedVoice = null;
        _rescan();
      });
    } else {
      window.speechSynthesis.onvoiceschanged = () => {
        cachedVoice = null;
        _rescan();
      };
    }
  } catch (_e) {
    /* ignore */
  }
}

/* -------------------- iOS Audio unlock -------------------- */
//
// iOS Safari blocks AudioContext / speechSynthesis until a user gesture. This
// helper must be called from INSIDE a user gesture (click/tap/touchstart).
// It's idempotent — subsequent calls are cheap no-ops.

let audioUnlocked = false;
let sharedAudioCtx = null;

/**
 * Unlocks Web Audio + Web Speech on the FIRST user gesture (iOS Safari).
 *
 *   1. Resume (or create) a shared AudioContext.
 *   2. Play a 0.01s silent buffer through it (primes iOS audio graph).
 *   3. Speak an empty utterance (primes speechSynthesis on iOS).
 *
 * Call this from onClick/onTouchStart of the very first voice-related button.
 * Idempotent: returns immediately after the first successful unlock.
 */
export function unlockAudio() {
  if (audioUnlocked) return true;
  if (typeof window === "undefined") return false;
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) {
      if (!sharedAudioCtx) {
        try {
          sharedAudioCtx = new AC();
        } catch (_e) {
          sharedAudioCtx = null;
        }
      }
      if (sharedAudioCtx && sharedAudioCtx.state === "suspended") {
        // fire-and-forget — resume returns a promise
        sharedAudioCtx.resume().catch(() => {});
      }
      if (sharedAudioCtx) {
        try {
          const buffer = sharedAudioCtx.createBuffer(
            1,
            Math.max(1, Math.floor(sharedAudioCtx.sampleRate * 0.01)),
            sharedAudioCtx.sampleRate
          );
          const src = sharedAudioCtx.createBufferSource();
          src.buffer = buffer;
          src.connect(sharedAudioCtx.destination);
          src.start(0);
        } catch (_e) {
          /* ignore */
        }
      }
    }
  } catch (_e) {
    /* ignore */
  }
  try {
    if (isTTSSupported()) {
      // Empty utterance primes the TTS engine on iOS without producing sound.
      const primer = new SpeechSynthesisUtterance("");
      primer.volume = 0;
      const v = pickSpanishVoice();
      if (v) primer.voice = v;
      primer.lang = v?.lang || "es-MX";
      // Some Safari versions ignore volume=0; cancel right after speak to
      // guarantee no audible artifact.
      window.speechSynthesis.speak(primer);
      window.speechSynthesis.cancel();
    }
  } catch (_e) {
    /* ignore */
  }
  audioUnlocked = true;
  return true;
}

/** Exposed for tests / debug. Never true until the first user gesture. */
export function isAudioUnlocked() {
  return audioUnlocked;
}

/** Exposed for hooks that need to attach an AnalyserNode to the same context. */
export function getSharedAudioContext() {
  return sharedAudioCtx;
}

/** Exposed for tests / debug — reports voice-list readiness. */
export function areVoicesReady() {
  return voicesReady;
}

/* -------------------- TTS / STT -------------------- */

export function speak(text, opts = {}) {
  return new Promise((resolve) => {
    if (!isTTSSupported() || !text) {
      resolve();
      return;
    }
    try {
      window.speechSynthesis.cancel();
    } catch (_e) {
      /* ignore */
    }
    const utter = new SpeechSynthesisUtterance(String(text));
    const voice = pickSpanishVoice();
    if (voice) utter.voice = voice;
    utter.lang = voice?.lang || "es-MX";
    utter.rate = opts.rate ?? 1.02;
    utter.pitch = opts.pitch ?? 1.0;
    utter.volume = opts.volume ?? 1.0;
    utter.onend = () => resolve();
    utter.onerror = () => resolve();
    try {
      window.speechSynthesis.speak(utter);
    } catch (_e) {
      resolve();
    }
  });
}

export function cancelSpeech() {
  if (!isTTSSupported()) return;
  try {
    window.speechSynthesis.cancel();
  } catch (_e) {
    /* ignore */
  }
}

// Start a one-shot STT recognition. Returns a stop() function.
// onResult receives { transcript, isFinal }.
// onError receives an error code string (e.g., "not-allowed", "no-speech").
// onEnd fires when the recognizer stops (naturally or via stop()).
export function startListening({ lang = "es-MX", onResult, onError, onEnd, continuous = false } = {}) {
  if (!SR) {
    onError?.("unsupported");
    onEnd?.();
    return () => {};
  }
  const rec = new SR();
  rec.lang = lang;
  rec.continuous = continuous;
  rec.interimResults = true;
  rec.maxAlternatives = 1;

  let stopped = false;

  rec.onresult = (event) => {
    let interim = "";
    let final = "";
    for (let i = event.resultIndex; i < event.results.length; i++) {
      const r = event.results[i];
      if (r.isFinal) final += r[0].transcript;
      else interim += r[0].transcript;
    }
    if (final) onResult?.({ transcript: final.trim(), isFinal: true });
    else if (interim) onResult?.({ transcript: interim.trim(), isFinal: false });
  };
  rec.onerror = (event) => {
    onError?.(event.error || "unknown");
  };
  rec.onend = () => {
    onEnd?.();
  };
  try {
    rec.start();
  } catch (_e) {
    onError?.("start-failed");
    onEnd?.();
  }
  return () => {
    if (stopped) return;
    stopped = true;
    try {
      rec.stop();
    } catch (_e) {
      /* ignore */
    }
  };
}

// Match utterance to a yes/no confirmation
export function isAffirmative(t) {
  const s = (t || "").toLowerCase().trim();
  return /\b(s[íi]|env[ií]a(lo|r)?|env[ií]alo|manda(lo)?|correcto|confirmo|dale|va|ok|okey)\b/.test(s);
}
export function isNegative(t) {
  const s = (t || "").toLowerCase().trim();
  return /\b(no|cancela(r)?|borra|olv[ií]dalo|para|detente)\b/.test(s);
}

// Simple command intent parser (Spanish, tolerant)
// Returns { intent, target? } or { intent: 'unknown' }
export function parseCommand(text) {
  const s = (text || "").toLowerCase().trim();
  if (!s) return { intent: "unknown" };

  if (/\b(sal[ií]r?|sal|apaga|desactiva|cerrar)\b.*\b(modo\s*conducci[oó]n|conducci[oó]n)?\b/.test(s)
      || /\bmodo\s*conducci[oó]n\b.*\b(desactiva|apaga|termina|salir)\b/.test(s)
      || /\b(desactiva|apaga|termina)\b.*\bconducci[oó]n\b/.test(s)
      || /^sal[ií]r?\.?$/.test(s)) {
    return { intent: "exit" };
  }

  // "opción uno/dos/tres" — pick a suggested response by number
  const optionMatch = s.match(/\b(?:opci[oó]n|opciones)\s*(?:n[uú]mero\s*)?(uno|1|dos|2|tres|3|primera|segunda|tercera)\b/);
  if (optionMatch) {
    const map = { uno: 1, "1": 1, primera: 1, dos: 2, "2": 2, segunda: 2, tres: 3, "3": 3, tercera: 3 };
    const num = map[optionMatch[1]];
    if (num) return { intent: "pick_option", option: num };
  }

  // "opciones" / "sugerencias" alone → ask for LLM suggestions
  if (/^(opciones|sugerencias|sug[eé]reme|sugi[eé]reme|dame\s+opciones)$/.test(s)) {
    return { intent: "suggest_options" };
  }

  if (/\b(lee|le[ée]r?me|dime|revisa|checa|d[ií]me)\b.*\b(mensajes?|bandeja|no\s*le[ií]dos)\b/.test(s)
      || /\b(qu[eé]|cu[aá]ntos)\s+(mensajes?|tengo)\b/.test(s)
      || /\btengo\s+mensajes?\b/.test(s)) {
    return { intent: "read_inbox" };
  }

  // "responde a X que ..." → capture target + rest as dictation
  const replyWith = s.match(/\b(responde|contesta|cont[eé]stale|escr[ií]bele|dile|m[aá]ndale)\b\s+(?:a\s+)?(?:@)?([\wáéíóúñü-]{2,20})\s+(?:que\s+|:)(.+)$/i);
  if (replyWith) {
    return { intent: "reply", target: replyWith[2].trim(), text: replyWith[3].trim() };
  }
  // "responde a X" without body
  const replyMatch = s.match(/\b(responde|contesta|cont[eé]stale|escr[ií]bele|dile|m[aá]ndale)\b\s+(?:a\s+)?(?:@)?([\wáéíóúñü\s-]{2,40})$/i);
  if (replyMatch) {
    return { intent: "reply", target: replyMatch[2].trim() };
  }

  return { intent: "unknown" };
}
